/**
 * Inbound-payments list (refreshed).
 *
 * Customer-initiated paybill / till receipts. MSISDN is delivered
 * already-masked by the backend (``payer_msisdn_masked``); the SPA
 * never sees full digits.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { ArrowDownToLine, Inbox, RotateCw } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { NameAvatar } from "@/components/shell/NameAvatar";
import { ListPagination } from "@/components/shell/ListPagination";
import { useEnvironment } from "@/lib/environment-context";
import { isMissingPage, LIST_PAGE_SIZE, usePageParam } from "@/lib/use-page-param";
import { CopyId } from "@/components/shell/CopyId";
import { RelativeTime } from "@/components/shell/RelativeTime";
import { DataTable, type DataTableColumn } from "@/components/shell/DataTable";
import { EmptyState } from "@/components/shell/EmptyState";
import { FilterChip } from "@/components/shell/FilterChip";
import { PageHeader } from "@/components/shell/PageHeader";
import { SearchInput } from "@/components/shell/SearchInput";
import { Toolbar, FilterGroup } from "@/components/shell/Toolbar";
import { InboundPaymentSheet } from "@/components/payments/InboundPaymentSheet";
import {
  getInboundPayments,
  type InboundPaymentRow,
  type InboundPaymentState,
} from "@/lib/api";
import { useAuth } from "@/lib/use-auth";
import { formatMinorUnits } from "@/lib/format";
import { INBOUND_STATE_BADGE_VARIANT } from "@/lib/state-badges";

const STATES: readonly InboundPaymentState[] = ["received", "reversed"] as const;

export function InboundPaymentsListPage() {
  const [params, setParams] = useSearchParams();
  const { page, setPage } = usePageParam();
  const { user } = useAuth();

  const { environment: env } = useEnvironment();
  const state = (params.get("state") as InboundPaymentState | null) ?? undefined;
  const q = params.get("q") ?? undefined;

  const [searchInput, setSearchInput] = useState(q ?? "");
  useEffect(() => {
    const t = setTimeout(() => {
      // Also skips the mount run, so a shared ?page=3 link keeps its page.
      if (searchInput === (params.get("q") ?? "")) return;
      const next = new URLSearchParams(params);
      if (searchInput) next.set("q", searchInput);
      else next.delete("q");
      next.delete("page");
      setParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const query = useQuery({
    queryKey: ["inbound-payments", { env, state, q, page }] as const,
    queryFn: () => getInboundPayments({ environment: env, state, q, page, page_size: LIST_PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (query.error && !isMissingPage(query.error, page)) {
      toast.error("Couldn't load inbound payments", {
        description: "Check your network and try again.",
      });
    }
  }, [query.error, page]);

  const [sheetId, setSheetId] = useState<string | null>(null);

  function setFilter(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next, { replace: true });
  }

  const hasFilters = Boolean(state || q);
  const isZeroGrantTenant =
    user?.role === "tenant" && user.tenants.length === 0 && !hasFilters;

  const columns = useMemo<ReadonlyArray<DataTableColumn<InboundPaymentRow>>>(
    () => [
      {
        key: "id",
        header: "Payment",
        cell: (r) => (
          <CopyId id={r.id} />
        ),
      },
      {
        key: "tenant",
        header: "Tenant",
        cell: (r) => (
          <div className="flex min-w-0 items-center gap-2">
            <NameAvatar name={r.tenant_name} />
            <span className="truncate font-medium">{r.tenant_name}</span>
            {r.tenant_environment === "test" ? <Badge variant="muted">Test</Badge> : null}
          </div>
        ),
      },
      {
        key: "payer",
        header: "Payer",
        cell: (r) => (
          <code className="font-mono text-xs">{r.payer_msisdn_masked || "—"}</code>
        ),
      },
      {
        key: "amount",
        header: "Amount",
        className: "text-right",
        cell: (r) => (
          <span className="whitespace-nowrap font-semibold tabular-nums">
            {formatMinorUnits(r.amount, r.currency)}
          </span>
        ),
      },
      {
        key: "account_ref",
        header: "Account ref",
        cell: (r) => <code className="font-mono text-xs">{r.account_ref || "—"}</code>,
      },
      {
        key: "state",
        header: "State",
        cell: (r) => (
          <Badge dot variant={INBOUND_STATE_BADGE_VARIANT[r.state]} className="capitalize">
            {r.state}
          </Badge>
        ),
      },
      {
        key: "received_at",
        header: "Received",
        className: "text-muted-foreground",
        cell: (r) => (
          <RelativeTime iso={r.received_at} />
        ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        icon={ArrowDownToLine}
        title="Inbound payments"
        description="Customer-initiated paybill / till receipts. Payer MSISDNs are masked at the API boundary."
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RotateCw className={query.isFetching ? "animate-spin" : undefined} />
            Refresh
          </Button>
        }
      />

      <Toolbar
        search={
          <SearchInput
            placeholder="Search by payment ID, provider ref, or account ref…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onClear={() => setSearchInput("")}
          />
        }
        filters={
          <>
            <FilterGroup label="State">
              <FilterChip
                active={state === undefined}
                onClick={() => setFilter("state", null)}
              >
                All
              </FilterChip>
              {STATES.map((s) => (
                <FilterChip
                  key={s}
                  active={state === s}
                  onClick={() => setFilter("state", s)}
                >
                  {s}
                </FilterChip>
              ))}
            </FilterGroup>
          </>
        }
      />

      <DataTable<InboundPaymentRow>
        stale={query.isPlaceholderData}
        columns={columns}
        rows={query.data?.results ?? []}
        loading={query.isLoading}
        rowKey={(r) => r.id}
        selectedKey={sheetId ?? undefined}
        onRowClick={(r) => setSheetId(r.id)}
        emptyState={
          isZeroGrantTenant ? (
            <EmptyState
              icon={Inbox}
              title="No tenants granted yet"
              description="Inbound payments appear here once a super-admin grants you access to a tenant."
            />
          ) : (
            <EmptyState
              icon={Inbox}
              title="No inbound payments match these filters"
              description="Customer payments to your paybill / till numbers appear here once the provider's callback lands."
              action={
                hasFilters ? (
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/inbound-payments">Clear filters</Link>
                  </Button>
                ) : undefined
              }
            />
          )
        }
      />

      <ListPagination query={query} page={page} onPage={setPage} />

      <InboundPaymentSheet
        id={sheetId}
        open={sheetId !== null}
        onOpenChange={(open) => {
          if (!open) setSheetId(null);
        }}
      />
    </>
  );
}

export default InboundPaymentsListPage;
